import { ArrowLeft, X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useShell, useShellSlot } from '../../shell/slots.tsx';
import { Modal } from '../Modal.tsx';
import { Button } from '../ui.tsx';
import { LIST_WIDTHS, recordModeOf } from './logic.ts';

/**
 * Lista com o item aberto (ADR 0128), arrumada pelo layout escolhido: ao lado da lista no Mestre
 * e detalhe, no painel da moldura em Três colunas e, nos outros, como o ajuste "abrir um item"
 * mandar (no lugar da lista, numa gaveta ou numa janela). A tela só diz o que é a lista e o que é
 * o item; nunca escolhe o jeito.
 */
export function ListDetail({
  list,
  detail,
  detailTitle,
  onClose,
  placeholder,
}: {
  list: ReactNode;
  /** O item aberto, ou null quando nenhum está. */
  detail: ReactNode | null;
  /** Nome do item aberto (título da gaveta ou janela e do painel). */
  detailTitle?: ReactNode;
  onClose: () => void;
  /** No Mestre e detalhe, o que aparece ao lado enquanto nenhum item está aberto. */
  placeholder?: ReactNode;
}) {
  const { t } = useTranslation();
  const screen = useShell();
  const mode = recordModeOf(screen?.shell ?? null, screen?.options ?? null);
  const open = detail != null;

  const panel = open ? (
    <DetailPanel title={detailTitle} onClose={onClose}>
      {detail}
    </DetailPanel>
  ) : undefined;
  const inSlot = useShellSlot('inspector', mode === 'inspector' ? panel : undefined);

  if (mode === 'split') {
    return (
      <div
        className={`grid min-h-0 gap-4 ${LIST_WIDTHS[screen!.options.listWidth]} md:items-start`}
      >
        <div className={`min-w-0 ${open ? 'max-md:hidden' : ''}`}>{list}</div>
        <div className={`min-w-0 ${open ? '' : 'max-md:hidden'}`}>
          {open ? (
            <DetailPanel title={detailTitle} onClose={onClose} back>
              {detail}
            </DetailPanel>
          ) : (
            <div className="ui-panel rounded-lg bg-sheet">{placeholder}</div>
          )}
        </div>
      </div>
    );
  }
  if (mode === 'inspector' && inSlot) return <>{list}</>;
  if (mode === 'page' || mode === 'inspector') {
    if (!open) return <>{list}</>;
    return (
      <div className="flex flex-col gap-3">
        <div>
          <Button variant="secondary" onClick={onClose}>
            <ArrowLeft size={16} aria-hidden />
            {t('patterns.backToList')}
          </Button>
        </div>
        {detail}
      </div>
    );
  }
  return (
    <>
      {list}
      {open && mode === 'modal' && (
        <Modal open title={detailTitle ?? ''} size="lg" onClose={onClose}>
          {detail}
        </Modal>
      )}
      {open && mode === 'drawer' && (
        <Drawer title={detailTitle} onClose={onClose}>
          {detail}
        </Drawer>
      )}
    </>
  );
}

/** O item aberto dentro de uma caixa com título e o botão de fechar. */
function DetailPanel({
  title,
  onClose,
  back = false,
  children,
}: {
  title?: ReactNode;
  onClose: () => void;
  /** No celular a caixa ocupa a tela: o fechar vira "voltar à lista". */
  back?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="ui-panel flex min-w-0 flex-col rounded-lg bg-sheet">
      <header className="flex items-center gap-2 border-b border-rule px-4 py-2.5">
        {back && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('patterns.backToList')}
            className="-ml-1 rounded-md p-1 text-muted hover:bg-paper hover:text-ink md:hidden"
          >
            <ArrowLeft size={16} aria-hidden />
          </button>
        )}
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('patterns.closeItem')}
          className={`rounded-md p-1 text-muted hover:bg-paper hover:text-ink ${back ? 'max-md:hidden' : ''}`}
        >
          <X size={16} aria-hidden />
        </button>
      </header>
      <div className="min-w-0 p-4">{children}</div>
    </section>
  );
}

/** Gaveta da direita por cima da tela: Esc ou o fundo fecham. */
function Drawer({
  title,
  onClose,
  children,
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-ink/30" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto bg-paper p-3 shadow-xl"
      >
        <DetailPanel title={title} onClose={onClose}>
          {children}
        </DetailPanel>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Os botões da tela (ADR 0128): na Planilha vão para a faixa de botões da moldura; nos outros
 * layouts ficam onde a tela os põe (em geral, no `PageHeader`). Devolve o que desenhar no corpo.
 */
export function useScreenActions(actions: ReactNode): ReactNode {
  const inRibbon = useShellSlot('ribbon', actions);
  return inRibbon ? null : actions;
}
